import React from 'react';
import { Coins, AlertTriangle, RefreshCw } from 'lucide-react';
import { useCredits } from '../../context/CreditsContext';
import { cn } from '../../lib/utils';

/**
 * Credit Balance Display - Shows current credit balance in the header/topbar
 * Updates in real-time after each action
 */
export function CreditBalance({ className, showRefresh = false }) {
  const { balance, loading, refreshBalance } = useCredits();
  
  const isLow = balance < 50;
  const isCritical = balance < 10;
  
  return (
    <div 
      className={cn(
        "flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors",
        isCritical ? "bg-red-500/20 border border-red-500/30" :
        isLow ? "bg-amber-500/20 border border-amber-500/30" :
        "bg-white/5 border border-white/10",
        className
      )}
      data-testid="credit-balance"
    >
      <Coins className={cn(
        "w-4 h-4",
        isCritical ? "text-red-400" :
        isLow ? "text-amber-400" :
        "text-brand-400"
      )} />
      
      {loading ? (
        <span className="text-sm text-white/50">...</span>
      ) : (
        <span className={cn(
          "text-sm font-semibold",
          isCritical ? "text-red-400" :
          isLow ? "text-amber-400" :
          "text-white"
        )}>
          {balance.toLocaleString()}
        </span>
      )}
      
      <span className="text-xs text-white/50">créditos</span>
      
      {isLow && !isCritical && (
        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 ml-1" />
      )}
      
      {isCritical && (
        <AlertTriangle className="w-3.5 h-3.5 text-red-400 ml-1" />
      )}
      
      {showRefresh && (
        <button 
          onClick={refreshBalance}
          className="ml-1 p-1 hover:bg-white/10 rounded transition-colors"
          title="Actualizar saldo"
        >
          <RefreshCw className="w-3 h-3 text-white/50" />
        </button>
      )}
    </div>
  );
}

/**
 * Credit Cost Badge - Shows cost for a specific action on buttons
 * Usage: <CreditCostBadge operation="generate_landing" />
 */
export function CreditCostBadge({ operation, quantity = 1, className }) {
  const { getCost } = useCredits();
  const cost = getCost(operation, quantity);
  
  if (cost === 0) return null;
  
  return (
    <span className={cn(
      "inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-white/10 text-white/70",
      className
    )}>
      <Coins className="w-3 h-3" />
      {cost}
    </span>
  );
}

/**
 * Credit Cost Text - Inline text showing cost
 * Usage: <CreditCostText operation="generate_landing" /> → "· 14 créditos"
 */
export function CreditCostText({ operation, quantity = 1, className, prefix = "·" }) {
  const { getCost } = useCredits();
  const cost = getCost(operation, quantity);
  
  if (cost === 0) return null;
  
  return (
    <span className={cn("text-white/50 text-sm", className)}>
      {prefix} {cost} crédito{cost !== 1 ? 's' : ''}
    </span>
  );
}

/**
 * Insufficient Credits Warning - Modal/Alert when user doesn't have enough credits
 */
export function InsufficientCreditsAlert({ 
  operation, 
  quantity = 1, 
  onBuyCredits,
  onClose 
}) {
  const { balance, getCost, getOperationName } = useCredits();
  const cost = getCost(operation, quantity);
  const missing = cost - balance;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl max-w-md w-full p-6 border border-red-500/30">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Créditos insuficientes</h3>
            <p className="text-sm text-white/60">No puedes realizar esta acción</p>
          </div>
        </div>
        
        <div className="bg-white/5 rounded-lg p-4 mb-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-white/70">Acción:</span>
            <span className="text-white font-medium">{getOperationName(operation)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-white/70">Coste:</span>
            <span className="text-white font-medium">{cost} créditos</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-white/70">Tu saldo:</span>
            <span className="text-red-400 font-medium">{balance} créditos</span>
          </div>
          <hr className="border-white/10" />
          <div className="flex justify-between text-sm">
            <span className="text-white/70">Te faltan:</span>
            <span className="text-amber-400 font-bold">{missing} créditos</span>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-white/20 rounded-lg text-white hover:bg-white/10 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onBuyCredits}
            className="flex-1 px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg font-medium transition-colors"
          >
            Comprar créditos
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to check credits before action
 * Returns: { canExecute, showWarning, execute }
 */
export function useCheckCredits(operation, quantity = 1) {
  const { hasEnoughCredits, refreshBalance, getCost } = useCredits();
  const [showInsufficientAlert, setShowInsufficientAlert] = React.useState(false);
  
  const cost = getCost(operation, quantity);
  const canExecute = hasEnoughCredits(operation, quantity);
  
  const checkAndExecute = React.useCallback(async (callback) => {
    if (!canExecute) {
      setShowInsufficientAlert(true);
      return false;
    }
    
    try {
      await callback();
      // Refresh balance after successful action
      await refreshBalance();
      return true;
    } catch (error) {
      // If it's a credit error, show the alert
      if (error.response?.status === 402 || 
          error.response?.data?.detail?.includes('créditos')) {
        setShowInsufficientAlert(true);
      }
      throw error;
    }
  }, [canExecute, refreshBalance]);
  
  return {
    canExecute,
    cost,
    showInsufficientAlert,
    setShowInsufficientAlert,
    checkAndExecute,
  };
}

export default CreditBalance;
